/* eslint-disable react/prefer-stateless-function */
import React from 'react';
import { connect } from 'react-redux';
import {
  LinearProgress, Select, InputLabel, MenuItem, Checkbox, Input, ListItemText, TextField, SelectChangeEvent,
} from '@mui/material';
import { IAppState } from '../../../../state/reducers';
import {
  IProps,
  IStateProps,
  IDispatchProps,
  IState,
} from './interfaces';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogActionsOverride,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import { VaultButton, Warning } from '../../../Shared';
import {
  SelectedContainer,
  QualifierSelect,
  ProgressContainer,
  VendorName,
  TextFilterContainer,
  DatePicker,
  VendorOptionsContainer,
  VendorsContainer,
} from './styles';
import {
  toggleVendorConsentSettings, save,
} from '../../../../state/exchange/vendorConsentSettings';
import { IVendor, IVendorTeam } from '../../../../api/vault/vendors';
import { deepCopy } from '../../../../utils/deepCopy';

class VendorConsentSettings extends React.Component<IProps> {
  state: IState = {
    filterText: '',
    currentVendors: [],
  };

  componentDidUpdate() {
    const { vendors } = this.props;
    const { currentVendors } = this.state;

    if (currentVendors.length === 0 && vendors && vendors.length > 0) {
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({
        currentVendors: deepCopy(vendors),
      });
    }
  }

  removeVendor(orgId: number) {
    const { currentVendors } = this.state;
    const index = currentVendors.findIndex((vendor) => vendor.orgId === orgId);
    if (index === -1) {
      console.log('Unable to find vendor');
      return;
    }
    currentVendors[index].teams = [];
    this.setState({
      currentVendors,
    });
  }

  addVendor(orgId: number) {
    const { currentVendors } = this.state;
    const index = currentVendors.findIndex((vendor) => vendor.orgId === orgId);
    if (index === -1) {
      console.log('Unable to find vendor');
      return;
    }
    currentVendors[index].teams.push({ exchangeQualifier: [] } as IVendorTeam);
    this.setState({
      currentVendors,
    });
  }

  updateVendorQualifiers(orgId: number, event: SelectChangeEvent<string[]>) {
    const { currentVendors } = this.state;
    const index = currentVendors.findIndex((vendor) => vendor.orgId === orgId);
    if (index === -1) {
      console.log('Unable to find vendor');
      return;
    }
    currentVendors[index].teams[0].exchangeQualifier = event.target.value as string[];
    // Sort qualifiers on update
    if (currentVendors[index].teams[0].exchangeQualifier.length > 0) {
      currentVendors[index].teams[0].exchangeQualifier.sort((a, b) => currentVendors[index].availableQualifiers.indexOf(a) - currentVendors[index].availableQualifiers.indexOf(b));
    }
    this.setState({
      currentVendors,
    });
  }

  updateVendorStartDate(orgId: number, date: Date) {
    const { currentVendors } = this.state;
    const index = currentVendors.findIndex((vendor) => vendor.orgId === orgId);
    if (index === -1) {
      console.log('Unable to find vendor');
      return;
    }

    currentVendors[index].teams[0].startGameDate = date;
    this.setState({
      currentVendors,
    });
  }

  render() {
    const {
      isLoading,
      vendors,
      onCancel,
      onSave,
    } = this.props;

    const {
      filterText,
      currentVendors,
    } = this.state;

    const vendorsExist = currentVendors && currentVendors.length > 0;

    const renderTextFilter = () => {
      const handleTextFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value !== filterText) {
          this.setState({
            filterText: e.target.value,
          });
        }
      };

      if (!vendorsExist) return null;

      return (
        <TextFilterContainer>
          <TextField
            id="outlined-secondary"
            placeholder="Filter by vendor name"
            variant="outlined"
            fullWidth
            onChange={handleTextFilter}
            value={filterText}
            data-auto="vendorFilter"
          />
        </TextFilterContainer>
      );
    };

    const filterVendors = (unfilteredVendors: IVendor[]): IVendor[] => {
      let filteredVendors: IVendor[] = [];
      if (filterText !== null) {
        const lowerCaseFilter = filterText.toLowerCase();
        filteredVendors = unfilteredVendors.filter((vendor) => {
          if (vendor.name.toLowerCase().includes(lowerCaseFilter)) {
            return true;
          }
          return false;
        });
      }
      return filteredVendors;
    };

    const renderVendors = () => {
      if (!vendorsExist) {
        return (
          <Warning
            isWarning={!vendorsExist || filterVendors(currentVendors).length === 0}
            message="No vendors found"
          />
        );
      }
      return (
        <VendorsContainer>
          {filterVendors(currentVendors).map((vendor: IVendor) => {
            const selected = vendor.teams.length > 0;
            let qualifiers: string[];
            let startDate: Date = null;
            if (selected) {
              qualifiers = vendor.teams[0].exchangeQualifier;
              if (vendor.teams[0].startGameDate) startDate = vendor.teams[0].startGameDate;
            }
            return (
              <SelectedContainer key={vendor.orgId} data-auto="ChecklistItem" vendor-name={vendor.name}>
                <Checkbox
                  checked={vendor.teams.length > 0}
                  data-checked={selected}
                  onChange={() => { selected ? this.removeVendor(vendor.orgId) : this.addVendor(vendor.orgId); }}
                  disabled={isLoading}
                />
                <VendorName>{vendor.name}</VendorName>
                {selected
                  && (
                    <VendorOptionsContainer>
                      <QualifierSelect data-auto="SelectVendorGameParts">
                        <InputLabel>
                          Select Game Parts
                        </InputLabel>
                        <Select
                          MenuProps={{ // Stops select from moving around after selecting an option
                          }}
                          multiple
                          value={qualifiers}
                          renderValue={(selectedQualifier) => (selectedQualifier as string[]).join(', ')}
                          input={<Input />}
                          onChange={(event) => { this.updateVendorQualifiers(vendor.orgId, event); }}
                          disabled={isLoading}
                        >
                          {vendor.availableQualifiers.map((qualifier: string) => (
                            <MenuItem key={qualifier} value={qualifier} data-auto="Qualifier" data-value={qualifier}>
                              <Checkbox checked={qualifiers.includes(qualifier)} />
                              <ListItemText primary={qualifier} />
                            </MenuItem>
                          ))}
                        </Select>
                      </QualifierSelect>
                      <DatePicker
                        format="MM-dd-yyyy"
                        value={startDate}
                        disableFuture
                        onChange={(date) => this.updateVendorStartDate(vendor.orgId, date as Date | null)}
                        label="Start Date"
                        data-auto="StartDateInput"
                        disabled={isLoading}
                        slotProps={{
                          actionBar: {
                            actions: ['clear', 'today'],
                          },
                          textField: {
                            helperText: startDate ? '' : 'Sharing Content From Current Season',
                          },
                        }}
                      />
                    </VendorOptionsContainer>
                  )}
              </SelectedContainer>
            );
          })}
          <Warning
            isWarning={filterVendors(vendors).length === 0}
            message="No vendors found"
          />
        </VendorsContainer>
      );
    };

    return (
      <>
        <DialogHeader>
          <DialogHeaderTitle data-auto="VendorConsentTitle">
            Vendor Consent
          </DialogHeaderTitle>
        </DialogHeader>
        <ProgressContainer>
          {isLoading && <LinearProgress />}
        </ProgressContainer>
        {renderTextFilter()}
        {renderVendors()}
        <DialogActionsOverride disableSpacing>
          <ActionContainer>
            <VaultButton
              variant="plainOnPaper"
              onClick={onCancel}
              data-auto="CancleBtn"
              disabled={isLoading}
            >
              Cancel
            </VaultButton>
            <VaultButton
              variant="primaryOnWhite"
              onClick={() => { onSave(currentVendors); }}
              data-auto="SubmitBtn"
              disabled={isLoading}
            >
              Save
            </VaultButton>
          </ActionContainer>
        </DialogActionsOverride>
      </>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isLoading: state.exchange.vendorConsentSettings.isLoading,
  vendors: state.exchange.vendorConsentSettings.vendors,
});

const mapDispatchToProps: IDispatchProps = {
  onCancel: toggleVendorConsentSettings,
  onSave: save,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(VendorConsentSettings);
